import React, { useImperativeHandle, useRef, useEffect } from "react";
import "./styles.scss";
import {
  Form,
  Input,
  InputNumber,
  Button,
  Checkbox,
  Select,
  Switch,
} from "antd";
import FilePicker from "../filePicker";
import PasswordInput from "../passwordInput";
import PhoneInput from "../phoneInput";

const adjectiveMap = {
  checkbox: "check",
  switch: "mark",
  select: "select",
};

/**
 * @description Form Component
 * @type component
 * @author Inderdeep
 */
const Main = React.forwardRef(
  (
    {
      elements = [],
      actions = [],
      onSubmit,
      onError,
      requiredMessage,
      formProps,
      submitButtonProps,
      hideSubmitButton,
      excludedFields = [],
      renderForm = null,
    },
    ref
  ) => {
    const [form] = Form.useForm();
    const formRef = useRef(null);
    const subscribeChanges = useRef([]);
    useImperativeHandle(ref, () => ({
      ...form,
      formElement: formRef.current,
      subscribe: (fn) => {
        subscribeChanges.current.push(fn);
      },
    }));

    const { getFieldsValue } = form;

    if (!elements instanceof Array) {
      elements = [];
      console.warn("Elements should be a array");
    }
    const handleSubmit = (values) => {
      values = { ...values };
      excludedFields.forEach((field) => {
        delete values[field];
      });
      onSubmit instanceof Function && onSubmit(values, form);
    };

    const renderElement = (element, index) => {
      if (!element) {
        return null;
      }
      let input = null;
      let {
        type,
        label,
        name,
        options,
        inputProps,
        optionProps,
        Component,
        data,
        required,
      } = element;
      let hideLabel = false;
      type = (type || "text").toLowerCase();
      inputProps = inputProps || {};
      options = {
        ...options,
      };
      options.rules = [...(options.rules || [])];
      inputProps.placeholder = inputProps.placeholder || label;
      switch (type) {
        case "text":
        case "email":
          input = (
            <Input
              type={type}
              className={`input ${inputProps.className || ""}`}
              maxLength={200}
              {...inputProps}
            />
          );
          if (
            type === "email" &&
            !options.rules.find((rule) => {
              return rule.type === "email";
            })
          ) {
            options.rules.push({
              type: "email",
              message:
                element.invalidMessage ||
                `Please enter a valid ${
                  element.label ? `${element.label.toLowerCase()}` : "value"
                }`,
            });
          }
          break;
        case "phone":
          input = (
            <PhoneInput
              className={`input ${inputProps.className || ""}`}
              {...inputProps}
            />
          );
          if (
            !options.rules.find((rule) => {
              return rule.min || rule.max || rule.pattern;
            })
          ) {
            options.rules.push({
              min: 10,
              message:
                element.invalidMessage || "Please enter a valid phone number",
            });
            options.rules.push({
              max: 20,
              message:
                element.invalidMessage || "Please enter a valid phone number",
            });
          }
          break;
        case "password":
          input = (
            <PasswordInput
              className={`input ${inputProps.className || ""}`}
              maxLength={200}
              {...inputProps}
            />
          );
          break;
        case "textarea":
          input = (
            <Input.TextArea
              className={`input ${inputProps.className || ""}`}
              maxLength={1000}
              {...inputProps}
            />
          );
          break;
        case "number":
          input = (
            <InputNumber
              className={`input ${inputProps.className || ""}`}
              {...inputProps}
            />
          );
          break;

        case "select":
          input = (
            <Select
              className={`select ${inputProps.className || ""}`}
              {...inputProps}
            >
              {(data || []).map((item, index) => {
                let { label, value } = item;
                if (typeof item === "string") {
                  label = item;
                  value = item;
                }
                return (
                  <Select.Option {...optionProps} key={index} value={value}>
                    {label}
                  </Select.Option>
                );
              })}
            </Select>
          );
          break;
        case "switch":
          input = <Switch {...inputProps}></Switch>;
          options.valuePropName = "checked";
          break;
        case "checkbox":
          hideLabel = true;
          options.valuePropName = "checked";
          input = <Checkbox {...inputProps}>{element.label}</Checkbox>;
          break;
        case "file":
          if (element.maxFileCount) {
            options.rules.push({
              validator: (rule, value, callback) => {
                if (
                  value instanceof Array &&
                  value.length > element.maxFileCount
                ) {
                  callback(
                    element.maxFilesMessage ||
                      `A Maximum ${element.maxFileCount} number of files are allowed`
                  );
                } else {
                  callback();
                }
              },
            });
          }
          const { name, label, type: elementType, ...rest } = element;
          input = (
            <FilePicker
              uploadProps={{
                accept: "*/*",
                multiple: false,
                ...inputProps,
              }}
              {...rest}
            ></FilePicker>
          );
          break;
        case "custom":
          delete inputProps.placeholder;
          input = <Component {...inputProps} />;
      }
      if (required) {
        options.rules.push({
          required: true,
          message:
            element.requiredMessage ||
            (requiredMessage &&
              requiredMessage.replace("{label}", element.label)) ||
            `Please ${adjectiveMap[type] || "enter"} ${
              element.label && typeof element.label === "string"
                ? `the ${element.label.toLowerCase()}`
                : "this field"
            }`,
          ...(type === "switch" || type === "checkbox"
            ? {
                transform: (value) => value || undefined, // Those two lines
                type: "boolean",
              }
            : {}),
        });
      }

      return (
        <React.Fragment
          key={typeof index === undefined ? Math.random() : index}
        >
          {element.before}
          <Form.Item
            hasFeedback={true}
            label={!hideLabel ? element.label || "" : undefined}
            {...element.formItemProps}
            name={element.name}
            {...options}
          >
            {input}
          </Form.Item>
          {element.after}
        </React.Fragment>
      );
    };
    const fieldValues = getFieldsValue();
    useEffect(() => {
      subscribeChanges.current.forEach((fn) => fn(fieldValues));
    }, [JSON.stringify(fieldValues)]);

    return (
      <Form
        layout={"vertical"}
        {...formProps}
        form={form}
        onFinishFailed={onError}
        onFinish={handleSubmit}
      >
        <div className="form" ref={formRef}>
          {renderForm
            ? renderForm({ renderElement })
            : elements.map((element, index) => {
                if (element.condition instanceof Function) {
                  element = {
                    ...element,
                    ...element.condition({
                      element,
                      values: fieldValues,
                    }),
                  };
                }
                if (
                  (element.type === "inline" || element.type === "section") &&
                  element.elements instanceof Array &&
                  element.elements.length
                ) {
                  return (
                    <div
                      className={
                        element.type === "inline" ? "inline-fields" : "section"
                      }
                      key={index}
                    >
                      {element.type === "section" ? (
                        <div className="section-header">
                          <p className="name">{element.label}</p>
                          <p className="description">{element.description}</p>
                        </div>
                      ) : null}
                      <div
                        className={
                          element.type === "inline" ? "inline" : "fields"
                        }
                      >
                        {element.elements.map((obj, i) => {
                          return renderElement(obj, i);
                        })}
                      </div>
                    </div>
                  );
                } else {
                  return renderElement(element, index);
                }
              })}
        </div>
        {hideSubmitButton && actions.length === 0 ? null : (
          <div className="actions">
            {!hideSubmitButton && (
              <Button
                type="primary"
                htmlType={"submit"}
                className="btn"
                {...submitButtonProps}
              >
                {(submitButtonProps && submitButtonProps.children) || "Submit"}
              </Button>
            )}
            {actions}
          </div>
        )}
      </Form>
    );
  }
);
Main.displayName = "Form-Component";
export default Main;
